import React from "react";
import StepWizard from "components/StepWizard";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { some, find, get } from "lodash";

import IconSpinner from "components/IconSpinner";
import type { RootState } from "types/state";
import AddressConfirmation from "./AddressConfirmation";
import DistributionAccountType from "./DistributionAccountType";
import DistributionAmount from "./DistributionAmount";
import DistributionType from "./DistributionType";
import DistributionFederalTax from "./DistributionFederalTax";
import DistributionStateTax from "./DistributionStateTax";
import DistributionSummary from "./DistributionSummary";
import DistributionReason from "./DistributionReason";
import DistributionSupport from "./DistributionSupport";
import type {
  DistributionAmountSelection,
  FederalTaxSelection,
  StateTaxSelection,
} from "./distributionTypes";

type OwnProps = Record<string, never>;

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { id, profile: userInfo } = state.user;
  const accounts = state.accounts.iraAccounts;

  return {
    id,
    userInfo,
    ownProps,
    accounts,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// Use PropsFromRedux directly (mirrors transactions/Transactions.tsx): merging
// the empty OwnProps `Record<string, never>` adds a string index signature that
// breaks `Omit<Props, "client">` inside withApollo's HOC types.
type Props = WithApolloClient<PropsFromRedux>;

interface State {
  isLoading: boolean;
  selectedAccountType: string;
  distributionAmountSelection: DistributionAmountSelection;
  // DistributionType may emit `undefined` (its initial computed type), so the
  // state field is widened to match what _handleDistributionTypeSelection sets.
  distributionType: string | undefined;
  distributionSpecialReason: string;
  federalTaxSelection: FederalTaxSelection;
  stateTaxSelection: StateTaxSelection;
  // written only by _onStepChange (mirrors react-step-wizard's onStepChange)
  activeStep?: number;
}

export class DistributionWorkflow extends React.PureComponent<Props, State> {
  // PRESERVED BUG (conversion-only): the StepWizard `instance` callback is
  // never wired up, so `this.wizard` is never assigned — `goToNamedStep`
  // below throws on undefined at call time. Declared (not initialized) to
  // preserve that throw-on-undefined behavior.
  wizard!: { goToNamedStep: (step: string) => void };

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      selectedAccountType: "",
      distributionAmountSelection: {
        distributionAmount: "",
        amount: "",
      },
      distributionType: "",
      distributionSpecialReason: "",
      federalTaxSelection: {
        federalTaxWithholdingPercentage: "",
        percentage: "",
      },
      stateTaxSelection: {
        stateTaxWithholding: "",
        stateTaxWithholdingPercentage: "",
        stateTaxWithholdingAmount: "",
      },
    };
  }

  async componentDidMount() {
    this.setState({
      isLoading: false,
    });
  }

  _onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  _handleAccountTypeSelection = (selectedAccountType: string) => {
    this.setState({ selectedAccountType });
  };

  _handleDistributionAmount = (
    distributionAmount: DistributionAmountSelection
  ) => {
    this.setState({
      distributionAmountSelection: {
        ...distributionAmount,
        isTotalDisbursement: distributionAmount.isTotalDisbursement || false,
      },
    });
  };

  // DistributionType emits `string | undefined` (its initial type can be
  // undefined); pass it straight through to state, matching the original JS.
  _handleDistributionTypeSelection = (distributionType: string | undefined) => {
    this.setState({ distributionType });
  };

  _handleDistributionReasonSelection = (distributionSpecialReason: string) => {
    this.setState({ distributionSpecialReason });
  };

  handleFederalTaxSelection = (federalTaxSelection: FederalTaxSelection) => {
    this.setState({ federalTaxSelection });
  };

  _handleStateTaxSelection = (stateTaxSelection: StateTaxSelection) => {
    this.setState({ stateTaxSelection });
  };

  render() {
    if (this.state.isLoading) {
      return <IconSpinner centered />;
    }

    const accountId = this.state.selectedAccountType;
    const isRothDistribution = some(this.props.accounts, {
      id: accountId,
      accountType: "ROTH_IRA",
    });

    const totalAccountBalance = get(
      find(this.props.accounts, { id: accountId }),
      "balances.totalAccountBalance"
    );

    return (
      <div className="mega-container" id="payroll-workflow">
        <div className="main-content">
          <StepWizard
            showNavigation={false}
            onStepChange={this._onStepChange}
            isLazyMount={true}
            transitions={{
              enterRight: "",
              enterLeft: "",
              exitRight: "",
              exitLeft: "",
            }}
          >
            <AddressConfirmation
              stepName="addressConfirmation"
              nextStepName="distributionAccountType"
              dob={this.props.userInfo.dob}
              accounts={this.props.accounts}
              isRothDistribution={isRothDistribution}
              selectedAccountType={this.state.selectedAccountType}
              goToNamedStep={(step) => this.wizard.goToNamedStep(step)}
            />
            <DistributionAccountType
              stepName="distributionAccountType"
              onAccountSelect={this._handleAccountTypeSelection}
            />
            <DistributionAmount
              stepName="distributionAmount"
              selectedAccountType={this.state.selectedAccountType}
              distributionAmount={this._handleDistributionAmount}
            />
            <DistributionType
              stepName="distributionType"
              dob={this.props.userInfo.dob}
              accounts={this.props.accounts}
              isRothDistribution={isRothDistribution}
              selectedAccountType={this.state.selectedAccountType}
              onDistributionTypeSelect={this._handleDistributionTypeSelection}
              onDistributionReasonSelect={
                this._handleDistributionReasonSelection
              }
            />
            <DistributionSupport
              stepName="distributionSupport"
              selectedAccountType={this.state.selectedAccountType}
              distributionType={this.state.distributionType}
              distributionAmount={this.state.distributionAmountSelection}
            />
            <DistributionReason
              stepName="distributionReason"
              onDistributionReasonSelect={
                this._handleDistributionReasonSelection
              }
            />
            <DistributionFederalTax
              stepName="distributionFederalTax"
              // profile carries an index signature (unknown fields); cast to
              // string at the boundary (same convention as DistributionStateTax).
              userState={this.props.userInfo.state as string}
              isRothDistribution={isRothDistribution}
              federalTaxSelection={this.state.federalTaxSelection}
              onFederalTaxSelection={this.handleFederalTaxSelection}
            />
            <DistributionStateTax
              stepName="distributionStateTax"
              userState={this.props.userInfo.state as string}
              isRothDistribution={isRothDistribution}
              // totalAccountBalance is Maybe<number>|undefined off the lodash
              // get; the original passed it straight through. Cast at boundary.
              accountValue={totalAccountBalance as number | undefined}
              // amount is stored as number|string; the original passed it
              // straight to withdrawalAmount (typed number). Cast at boundary.
              withdrawalAmount={
                this.state.distributionAmountSelection.amount as number
              }
              federalTaxSelection={this.state.federalTaxSelection}
              isTotalDisbursement={
                this.state.distributionAmountSelection.isTotalDisbursement
              }
              onStateTaxSelection={this._handleStateTaxSelection}
              isAccountClosure={false}
            />
            <DistributionSummary
              stepName="distributionSummary"
              isRothDistribution={isRothDistribution}
              selectedAccountType={this.state.selectedAccountType}
              distributionAmountSelection={
                this.state.distributionAmountSelection
              }
              distributionType={this.state.distributionType}
              distributionSpecialReason={this.state.distributionSpecialReason}
              federalTaxSelection={this.state.federalTaxSelection}
              stateTaxSelection={this.state.stateTaxSelection}
              isTotalDisbursement={
                this.state.distributionAmountSelection.isTotalDisbursement
              }
            />
          </StepWizard>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DistributionWorkflow));
